import { createMailtoLink, EMAIL_REGEX_GLOBAL } from "@redotech/email";
import classNames from "classnames";
import { domAnimation, LazyMotion } from "motion/react";
import * as m from "motion/react-m";
import { Fragment, memo, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as styles from "./redo-chat-text.module.css";

export interface RedoChatTextProps {
  name?: string;
  isResponder: boolean;
  chatBlock: string[];
  htmlContent?: string | null;
  files?: string[];
  shouldAnimate?: boolean;
  timestamp?: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      // stagger the animation of children for a quick fade in effect, kinda like Gemini
      staggerChildren: 0.04,
    },
  },
};

const wordVariants = {
  hidden: { opacity: 0, y: 5 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

// this is based on the long URL breaking strategy here: https://css-tricks.com/better-line-breaks-for-long-urls/
const formatUrlForBreaking = (url: string): React.ReactNode[] => {
  const doubleSlash = url.split("//");
  // Format the strings on either side of double slashes separately
  const formatted = doubleSlash.map((str, index) => {
    let result = str;
    // Insert a word break opportunity after a colon
    result = result.replace(/(:)/g, "$1\u200B");
    // Before a single slash, tilde, period, comma, hyphen, underline, question mark, number sign, or percent symbol
    result = result.replace(/([/~.,\-_?#%])/g, "\u200B$1");
    // Before and after an equals sign or ampersand
    result = result.replace(/([=&])/g, "\u200B$1\u200B");
    // Split by zero-width space and create React nodes
    const parts = result.split("\u200B");
    return parts.map((part, partIndex) => (
      <Fragment key={`${index}-${partIndex}`}>{part}</Fragment>
    ));
  });

  // Reconnect the strings with word break opportunities after double slashes
  const finalResult: React.ReactNode[] = [];
  for (const [index, parts] of formatted.entries()) {
    finalResult.push(...parts);
    if (index < formatted.length - 1) {
      // eslint-disable-next-line react/jsx-no-comment-textnodes
      finalResult.push(<Fragment key={`slash-${index}`}>//</Fragment>);
    }
  }

  return finalResult;
};

const formatEmailForBreaking = (email: string): React.ReactNode[] => {
  // Format email similar to URLs for proper line breaking
  // Insert word break opportunities before @ and dots
  let result = email;
  result = result.replace(/(@)/g, "\u200B$1");
  result = result.replace(/(\.)/g, "\u200B$1");
  // Split by zero-width space and create React nodes
  const parts = result.split("\u200B");
  return parts.map((part, partIndex) => (
    <Fragment key={`email-part-${email}-${partIndex}`}>{part}</Fragment>
  ));
};

const RedoChatImage = memo(function RedoChatImage({
  isResponder,
  imgSrc,
}: {
  isResponder: boolean;
  imgSrc: string;
}) {
  const imageContainerStyle = !isResponder
    ? {
        backgroundColor:
          "var(--redo-component-colors-components-buttons-primary-button-primary-bg)",
        backgroundImage: `linear-gradient(
          170deg,
          rgba(255, 255, 255, 0.15),
          rgba(0, 0, 0, 0.15)
        )`,
      }
    : undefined;

  return (
    <div style={{ maxWidth: "85%", display: "flex", flexDirection: "column" }}>
      <Flex
        bgColor={isResponder ? "tertiary" : undefined}
        borderColor={isResponder ? "secondary" : "none"}
        borderStyle={isResponder ? "solid" : "none"}
        borderWidth={isResponder ? "1px" : "none"}
        p="xs"
        radius="sm"
        style={imageContainerStyle}
        topLeftRadius={isResponder ? "none" : "sm"}
        topRightRadius={isResponder ? "sm" : "none"}
        w="fit-content"
      >
        <img
          alt="Chat attachment"
          src={imgSrc}
          style={{ maxWidth: "100%", maxHeight: "300px", borderRadius: "4px" }}
        />
      </Flex>
    </div>
  );
});

const extractNodes = (
  text: string,
  files: string[],
  shouldAnimate: boolean,
  isResponder: boolean,
) => {
  const nodes: React.ReactNode[] = [];
  const images: React.ReactNode[] = files.map((file, index) => (
    <RedoChatImage
      imgSrc={file}
      isResponder={isResponder}
      key={`file-${index}`}
    />
  ));

  const urlRegex =
    /((?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$]))/gim;
  const imageRegex = /(https?:\/\/\S+(\.png|\.jpg|\.gif|\.jpeg|\.webp))/g;
  // we create this new regex instance to avoid lastIndex issues. EMAIL_REGEX_GLOBAL is a shared constant; if other code uses .test()
  // on EMAIL_REGEX_GLOBAL without resetting lastIndex, the search will start from the wrong position
  const emailRegex = new RegExp(
    EMAIL_REGEX_GLOBAL.source,
    EMAIL_REGEX_GLOBAL.flags,
  );
  const messageArray = text.split(urlRegex);

  for (const [partIndex, part] of messageArray.entries()) {
    // Reset regex lastIndex to avoid issues with global flag
    urlRegex.lastIndex = 0;
    imageRegex.lastIndex = 0;
    emailRegex.lastIndex = 0;

    const isUrl = urlRegex.test(part);
    const isImage = imageRegex.test(part);

    if (isUrl) {
      if (isImage) {
        images.push(
          <RedoChatImage
            imgSrc={part}
            isResponder={isResponder}
            key={`image-${partIndex}`}
          />,
        );
      } else {
        nodes.push(
          <m.a
            animate={shouldAnimate ? "visible" : false}
            className={styles.chatLink}
            href={part}
            initial={shouldAnimate ? "hidden" : "visible"}
            key={`link-${partIndex}`}
            rel="noopener noreferrer"
            style={{ wordBreak: "break-all" }}
            target="_blank"
            variants={containerVariants}
          >
            {formatUrlForBreaking(part)}
          </m.a>,
        );
      }
    } else if (part.trim()) {
      // Check if this part contains emails
      emailRegex.lastIndex = 0;
      const isEmail = emailRegex.test(part);

      if (isEmail) {
        // Split by emails and process each segment
        emailRegex.lastIndex = 0;
        const segments = part.split(emailRegex);
        const emails = part.match(emailRegex) || [];

        let partSegmentIndex = 0;
        for (const [i, segment] of segments.entries()) {
          // only handling segments BEFORE emails; those that follow get handled by normal text processing
          if (i < emails.length && segment.trim()) {
            if (shouldAnimate) {
              nodes.push(
                <m.p
                  animate={shouldAnimate ? "visible" : false}
                  initial={shouldAnimate ? "hidden" : "visible"}
                  key={`text-${partIndex}-${partSegmentIndex}`}
                  variants={containerVariants}
                >
                  {segment.split(" ").map((word, wordIndex) => (
                    <m.span
                      key={`word-${partIndex}-${partSegmentIndex}-${wordIndex}`}
                      style={{ display: "inline-block" }}
                      variants={wordVariants}
                    >
                      {word + "\u00A0"}
                    </m.span>
                  ))}
                </m.p>,
              );
            } else {
              nodes.push(
                <Fragment key={`plain-text-${partIndex}-${partSegmentIndex}`}>
                  {segment}
                </Fragment>,
              );
            }
            partSegmentIndex++;
          }

          // Add email link if there's a matching email
          if (i < emails.length) {
            const email = emails[i];
            try {
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              const mailtoLink = createMailtoLink({ to: [email] });
              nodes.push(
                <m.a
                  animate={shouldAnimate ? "visible" : false}
                  className={styles.chatLink}
                  href={mailtoLink}
                  initial={shouldAnimate ? "hidden" : "visible"}
                  key={`email-${partIndex}-${partSegmentIndex}`}
                  rel="noopener noreferrer"
                  style={{ wordBreak: "break-all" }}
                  target="_blank"
                  variants={containerVariants}
                >
                  {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
                  {formatEmailForBreaking(email)}
                </m.a>,
              );
              partSegmentIndex++;
            } catch {
              // Skip invalid emails
            }
          }
        }

        // Handle trailing segments (text after the last email) with normal text processing
        // without this, characters suffixing to emails without whitespace get handled incorrectly.
        const trailingSegment = segments[segments.length - 1];
        if (
          trailingSegment &&
          trailingSegment.trim() &&
          segments.length > emails.length
        ) {
          if (shouldAnimate) {
            nodes.push(
              <m.p
                animate={shouldAnimate ? "visible" : false}
                initial={shouldAnimate ? "hidden" : "visible"}
                key={`text-trailing-${partIndex}`}
                variants={containerVariants}
              >
                {trailingSegment.split(" ").map((word, wordIndex) => (
                  <m.span
                    key={`word-trailing-${partIndex}-${wordIndex}`}
                    style={{ display: "inline-block" }}
                    variants={wordVariants}
                  >
                    {word + "\u00A0"}
                  </m.span>
                ))}
              </m.p>,
            );
          } else {
            nodes.push(
              <Fragment key={`plain-text-trailing-${partIndex}`}>
                {trailingSegment}
              </Fragment>,
            );
          }
        }
      } else if (shouldAnimate) {
        nodes.push(
          <m.p
            animate={shouldAnimate ? "visible" : false}
            initial={shouldAnimate ? "hidden" : "visible"}
            key={`text-${partIndex}`}
            variants={containerVariants}
          >
            {part.split(" ").map((word, wordIndex) => (
              <m.span
                key={`word-${partIndex}-${wordIndex}`}
                style={{ display: "inline-block" }}
                variants={wordVariants}
              >
                {word + "\u00A0"}
              </m.span>
            ))}
          </m.p>,
        );
      } else {
        nodes.push(<Fragment key={`plain-text-${partIndex}`}>{part}</Fragment>);
      }
    }
  }

  return { textNodes: nodes, images };
};

function shallowArrayEqual(
  a: string[] | undefined,
  b: string[] | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (const [i, element] of a.entries()) {
    if (element !== b[i]) return false;
  }
  return true;
}

function areRedoChatTextPropsEqual(
  prev: RedoChatTextProps,
  next: RedoChatTextProps,
): boolean {
  return (
    prev.name === next.name &&
    prev.isResponder === next.isResponder &&
    prev.shouldAnimate === next.shouldAnimate &&
    prev.htmlContent === next.htmlContent &&
    prev.timestamp === next.timestamp &&
    shallowArrayEqual(prev.chatBlock, next.chatBlock) &&
    shallowArrayEqual(prev.files, next.files)
  );
}

export const RedoChatText = memo(function RedoChatText({
  name,
  isResponder,
  chatBlock,
  htmlContent,
  shouldAnimate,
  timestamp,
  files,
}: RedoChatTextProps) {
  const [showTimestamp, setShowTimestamp] = useState(false);

  const textContainerStyle = !isResponder
    ? {
        backgroundColor:
          "var(--redo-component-colors-components-buttons-primary-button-primary-bg)",
        backgroundImage: `linear-gradient(
          170deg,
          rgba(255, 255, 255, 0.15),
          rgba(0, 0, 0, 0.15)
        )`,
      }
    : undefined;

  const timestampStyle = {
    position: "absolute" as const,
    top: "100%",
    left: isResponder ? "2px" : "auto",
    right: isResponder ? "auto" : "2px",
    transformOrigin: "top",
    opacity: 0.8,
    fontSize: "0.7rem",
    whiteSpace: "nowrap" as const,
    pointerEvents: "none" as const,
  };

  return (
    <LazyMotion features={domAnimation}>
      <Flex flexDirection="column" gap="sm" w="full">
        {name && (
          <Text fontSize="xs" fontWeight="thin">
            {name}
          </Text>
        )}
        {chatBlock.map((text, index) => {
          let contentElement: React.ReactNode;
          let images: React.ReactNode[] = [];
          if (htmlContent) {
            contentElement = (
              <div
                className={classNames(styles.chatText, styles.chatHtmlText)}
                dangerouslySetInnerHTML={{ __html: htmlContent }} //this html comes from the merchant app. we store it directly in the db from quill.
              />
            );
          } else {
            const { textNodes, images: extractedImages } = extractNodes(
              text,
              files ?? [],
              shouldAnimate ?? false,
              isResponder,
            );
            images = extractedImages;

            if (textNodes.length > 0) {
              contentElement = (
                <Text
                  as="div"
                  className={styles.chatText}
                  fontSize="sm"
                  fontWeight="regular"
                  textColor={isResponder ? "primary" : "primary-on-brand"}
                >
                  {textNodes.map((textNode, textIndex) => (
                    <Fragment key={`${textIndex}`}>{textNode}</Fragment>
                  ))}
                </Text>
              );
            }
          }

          return (
            <Flex
              align={isResponder ? "flex-start" : "flex-end"}
              flexDirection="column"
              gap="xs"
              key={`chat-block-${index}`}
            >
              {contentElement && (
                <m.div
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  initial={{ opacity: 0, y: 10, scale: 0.9 }}
                  onMouseEnter={() => setShowTimestamp(true)}
                  onMouseLeave={() => setShowTimestamp(false)}
                  style={{
                    maxWidth: "85%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                    staggerChildren: 0.1,
                  }}
                >
                  <Flex
                    bgColor={isResponder ? "tertiary" : undefined}
                    borderColor={isResponder ? "secondary" : "none"}
                    borderStyle={isResponder ? "solid" : "none"}
                    borderWidth={isResponder ? "1px" : "none"}
                    p="md"
                    radius="sm"
                    style={textContainerStyle}
                    topLeftRadius={isResponder ? "none" : "sm"}
                    topRightRadius={isResponder ? "sm" : "none"}
                    w="fit-content"
                  >
                    {contentElement}
                  </Flex>

                  {timestamp && (
                    <m.p
                      animate={{
                        opacity: showTimestamp ? 0.6 : 0,
                        scaleY: showTimestamp ? 1 : 0,
                      }}
                      initial={{ opacity: 0, scaleY: 0 }}
                      style={timestampStyle}
                      transition={{
                        duration: 0.2,
                        ease: "easeOut",
                        delay: showTimestamp ? 0.3 : 0,
                      }}
                    >
                      {timestamp}
                    </m.p>
                  )}
                </m.div>
              )}
              {images.map((image, imageIndex) => (
                <Fragment key={`image-fragment-${imageIndex}`}>
                  {image}
                </Fragment>
              ))}
            </Flex>
          );
        })}
      </Flex>
    </LazyMotion>
  );
}, areRedoChatTextPropsEqual);
